import app from 'flarum/forum/app';
import { extend, override } from 'flarum/common/extend';
import IndexPage from 'flarum/forum/components/IndexPage';
import HeaderPrimary from 'flarum/forum/components/HeaderPrimary';
import HeaderSecondary from 'flarum/forum/components/HeaderSecondary';
import Page from 'flarum/common/components/Page';
import LinkButton from 'flarum/common/components/LinkButton';
import Club from './models/Club';
import Artist from './models/Artist';
import Figure from './models/Figure';
import ClubsPage from './components/ClubsPage';
import ClubPage from './components/ClubPage';
import DarkStory from './models/DarkStory';
import DarkPagesPage from './components/dark/DarkPagesPage';
import DarkStoryPage from './components/dark/DarkStoryPage';
import clubDarkPages from './components/dark/clubDarkPages';
import ArtistsPage from './components/ArtistsPage';
import ArtistPage from './components/ArtistPage';
import FiguresPage from './components/FiguresPage';
import FigurePage from './components/FigurePage';
import trackVisits from './analytics';
import LanguageSwitcher from './components/LanguageSwitcher';
import { registerRoutes, route, tt, navLocale, isPublicPage, currentLocale, defaultLocale, localizePath } from './i18n';

app.initializers.add('legendary-clubs', () => {
  // Flarum zet in zijn basislayout maximum-scale=1/minimum-scale=1, waardoor je op een
  // telefoon niet kunt inzoomen op een flyer, een krantenknipsel of kleine tekst. Dat is
  // een toegankelijkheidsprobleem (WCAG 1.4.4); hier zetten we het zoomen weer vrij.
  const viewport = document.querySelector('meta[name="viewport"]');
  if (viewport) viewport.setAttribute('content', 'width=device-width, initial-scale=1, viewport-fit=cover');

  // Eigen, cookieloze bezoekersregistratie (zie analytics.js).
  trackVisits();

  app.store.models['lc-clubs'] = Club;
  app.store.models['lc-dark-stories'] = DarkStory;
  ClubPage.prototype.darkPages = clubDarkPages;
  app.store.models['lc-artists'] = Artist;
  app.store.models['lc-figures'] = Figure;

  app.routes['lc.clubs'] = { path: '/clubs', component: ClubsPage };
  app.routes['lc.club'] = { path: '/clubs/:slug', component: ClubPage };
  app.routes['lc.dark'] = { path: '/dark-pages', component: DarkPagesPage };
  app.routes['lc.dark.story'] = { path: '/dark-pages/:slug', component: DarkStoryPage };
  app.routes['lc.artists'] = { path: '/artiesten', component: ArtistsPage };
  app.routes['lc.artist'] = { path: '/artiesten/:slug', component: ArtistPage };
  app.routes['lc.figures'] = { path: '/faces', component: FiguresPage };
  app.routes['lc.figure'] = { path: '/faces/:slug', component: FigurePage };
  // oude adressen (vóór /faces) blijven werken binnen de app
  app.routes['lc.figures.old'] = { path: '/figuren', component: FiguresPage };
  app.routes['lc.figure.old'] = { path: '/figuren/:slug', component: FigurePage };

  // Dezelfde pagina's in het Frans en Engels (/fr/..., /en/...); paden uit I18n\Locale::ROUTES.
  registerRoutes({
    'lc.home': ClubsPage,
    'lc.clubs': ClubsPage,
    'lc.club': ClubPage,
    'lc.artists': ArtistsPage,
    'lc.artist': ArtistPage,
    'lc.figures': FiguresPage,
    'lc.figure': FigurePage,
    'lc.dark': DarkPagesPage,
    'lc.dark.story': DarkStoryPage,
  });

  // <html lang>: de taal van het adres op het publieke deel, Nederlands op het forum.
  // Klasse LcPublic op <body> voor stijlen die enkel op het publieke deel gelden (de kop met taalkeuze).
  extend(Page.prototype, 'oninit', () => {
    const isPublic = isPublicPage();
    document.documentElement.lang = isPublic ? currentLocale() : defaultLocale();
    document.body.classList.toggle('LcPublic', isPublic);
  });

  // Taalkeuze in de kop, enkel op het publieke deel (het forum blijft Nederlands).
  extend(HeaderSecondary.prototype, 'items', (items) => {
    if (!isPublicPage()) return;
    if (items.has('locale')) items.remove('locale');
    items.add('lc-language', <LanguageSwitcher />, 35);
  });

  // De menulinks (fof/links, in de database in het Nederlands) in de taal van de pagina.
  extend(HeaderPrimary.prototype, 'items', localizeHeaderLinks);

  // Link naar het clubarchief bovenaan de zijbalk van het forum.
  extend(IndexPage.prototype, 'navItems', (items) => {
    items.add(
      'lc-clubs',
      <LinkButton href={route('lc.clubs', {}, navLocale())} icon="fas fa-map-marked-alt">
        {app.translator.trans('legendary-clubs.forum.nav')}
      </LinkButton>,
      110
    );
    items.add(
      'lc-artists',
      <LinkButton href={route('lc.artists', {}, navLocale())} icon="fas fa-headphones">
        {app.translator.trans('legendary-clubs.forum.artists_nav')}
      </LinkButton>,
      109
    );
    items.add(
      'lc-figures',
      <LinkButton href={route('lc.figures', {}, navLocale())} icon="fas fa-user-friends">
        {app.translator.trans('legendary-clubs.forum.figures_nav')}
      </LinkButton>,
      108
    );
    items.add(
      'lc-dark',
      <LinkButton href={route('lc.dark', {}, navLocale())} icon="fas fa-newspaper">
        The Dark Pages
      </LinkButton>,
      100
    );
  });
});

// Nederlandse titels van de menulinks => vertaalsleutel. Een link die de beheerder hernoemt,
// valt hier buiten en blijft gewoon zoals ze in de database staat.
const HEADER_TITLES = {
  Clubs: 'header.clubs',
  "DJ's & producers": 'header.artists',
  'Alle artiesten': 'header.artists_all',
  "DJ's": 'header.djs',
  Producers: 'header.producers',
  Projecten: 'header.projects',
  Figuren: 'header.figures',
  'Dark Pages': 'header.dark',
  Forum: 'header.forum',
};

let headerPatched = false;

/**
 * fof/links haalt titel en adres uit het model; we vertalen de titel naar de taal van de
 * pagina en sturen links naar het publieke deel naar dezelfde taal (op het forum: de
 * gekozen voorkeur). Forumlinks (zoals /all) blijven ongemoeid.
 */
function localizeHeaderLinks() {
  const LinkModel = app.store.models.links;
  if (headerPatched || !LinkModel) return;
  headerPatched = true;

  override(LinkModel.prototype, 'title', function (original) {
    const title = original();
    return HEADER_TITLES[title] && currentLocale() !== defaultLocale() ? tt(HEADER_TITLES[title]) : title;
  });

  override(LinkModel.prototype, 'url', function (original) {
    const url = original();
    const locale = navLocale();
    if (!url || locale === defaultLocale()) return url;
    const match = url.match(/^(https?:\/\/[^/]+)?(\/.*)$/);
    return match ? (match[1] || '') + localizePath(match[2], locale) : url;
  });
}
